import {
  consumedToolDeploymentToJsonApiDocument,
  listConsumedToolDeploymentsResponseToJsonApiDocument,
} from '@efdigital/ll-admin-v2/data/handlers/lti/lti-consumed-tool-deployment-handler.ts';
import {
  consumedToolToJsonApiDocument,
  listConsumedToolsResponseToJsonApiDocument,
} from '@efdigital/ll-admin-v2/data/handlers/lti/lti-consumed-tool-handler.ts';
import {
  consumingPlatformToJsonApiDocument,
  isLtiRegistrationUrl,
  listConsumingPlatformsResponseToJsonApiDocument,
} from '@efdigital/ll-admin-v2/data/handlers/lti/lti-consuming-platform-handler.ts';
import type { ResourceLinkQueryResponse } from '@efdigital/ll-admin-v2/data/handlers/lti/lti-resource-link-handler.ts';
import {
  listResourceLinksByCohortResponseToJsonApiDocument,
  listResourceLinksResponseToJsonApiDocument,
  resourceLinkToJsonApiDocument,
} from '@efdigital/ll-admin-v2/data/handlers/lti/lti-resource-link-handler.ts';
import type {
  ConsumedToolCreateResponse,
  ConsumedToolDeploymentCreateResponse,
  ConsumedToolDeploymentListResponse,
  ConsumedToolListResponse,
  ConsumingPlatformCreateResponse,
  ConsumingPlatformGetResponse,
  ConsumingPlatformListResponse,
  ResourceLinkCreateResponse,
  ResourceLinkListResponse,
} from '@efdigital/ll-cdk-lti-registration-api-types';
import type { Handler, NextFn } from '@warp-drive/core/request';
import type {
  RequestContext,
  StructuredDataDocument,
} from '@warp-drive/core/types/request';

import { LtiOperation } from '../builders/lti/ops.ts';

export { LTI_API_PATH } from '@efdigital/ll-admin-v2/data/handlers/lti/lti-consuming-platform-handler.ts';

export class LtiRegistrationHandler implements Handler {
  async request<T = unknown>(
    context: RequestContext,
    next: NextFn<T>
  ): Promise<T | StructuredDataDocument<T>> {
    const { request } = context;

    if (!isLtiRegistrationUrl(request.url)) {
      return next(request);
    }

    const payload = await next(request);
    if (payload === null) {
      return null as T | StructuredDataDocument<T>;
    }

    switch (request.op) {
      case LtiOperation.ListConsumingPlatforms: {
        const content =
          payload.content as unknown as ConsumingPlatformListResponse;
        return listConsumingPlatformsResponseToJsonApiDocument(content) as T;
      }
      case LtiOperation.FindConsumingPlatform: {
        const content =
          payload.content as unknown as ConsumingPlatformGetResponse;
        return consumingPlatformToJsonApiDocument(content) as T;
      }
      case LtiOperation.ListConsumedTools: {
        const content = payload.content as unknown as ConsumedToolListResponse;
        return listConsumedToolsResponseToJsonApiDocument(content) as T;
      }
      case LtiOperation.ListConsumedToolDeployments: {
        const content =
          payload.content as unknown as ConsumedToolDeploymentListResponse;
        return listConsumedToolDeploymentsResponseToJsonApiDocument(
          content
        ) as T;
      }
      case LtiOperation.ListResourceLinks: {
        const content = payload.content as unknown as ResourceLinkListResponse;
        return listResourceLinksResponseToJsonApiDocument(content) as T;
      }
      case LtiOperation.ListResourceLinksByCohort: {
        const content = payload.content as unknown as ResourceLinkQueryResponse;
        return listResourceLinksByCohortResponseToJsonApiDocument(content) as T;
      }
      case 'createRecord':
      case 'updateRecord': {
        const url = request.url ?? '';
        const content = payload.content;

        if (url.includes('/resource-links')) {
          return resourceLinkToJsonApiDocument(
            content as unknown as ResourceLinkCreateResponse
          ) as T;
        }

        if (url.includes('/deployments')) {
          return consumedToolDeploymentToJsonApiDocument(
            content as unknown as ConsumedToolDeploymentCreateResponse
          ) as T;
        }

        if (url.includes('/tools')) {
          return consumedToolToJsonApiDocument(
            content as unknown as ConsumedToolCreateResponse
          ) as T;
        }

        return consumingPlatformToJsonApiDocument(
          content as unknown as ConsumingPlatformCreateResponse
        ) as T;
      }
      default:
        return payload;
    }
  }
}

export const ltiRegistrationHandler = new LtiRegistrationHandler();
